import posthog from "posthog-js";
import { PDF_TOOLS } from "./pdf-tools";
import type { PdfInputFormat } from "./pdf-import";

export type PdfEntrySource = "upload" | "sample";

interface Attempt {
  attemptId: string;
  source: PdfEntrySource;
  inputFormat: PdfInputFormat;
  entry: string;
  editObserved: boolean;
  exports: Set<string>;
}

export function createPdfFirstUseTracker(
  capture: (event: string, properties: Record<string, unknown>) => void,
) {
  const attempts = new Map<string, Attempt>();
  const entries = new Set<string>();
  const emit = (event: string, properties: Record<string, unknown>) => {
    const entry = typeof properties.entry === "string" &&
      Object.prototype.hasOwnProperty.call(PDF_TOOLS, properties.entry)
      ? properties.entry : "editor";
    try {
      capture(event, {
        flow: "pdf_first_use_v1",
        ...properties,
        entry,
        $current_url: `/${entry}`,
        $pathname: `/${entry}`,
        $referrer: "",
        referrer: "",
        landing_page: `/${entry}`,
      });
    } catch {
      // Analytics must never interrupt editing or exporting.
    }
  };
  return {
    entry(entry: string) {
      if (entries.has(entry)) return;
      entries.add(entry);
      emit("pdf_entry_viewed", { entry });
    },
    opened(documentId: string, source: PdfEntrySource, entry: string, inputFormat: PdfInputFormat = "pdf") {
      if (attempts.has(documentId)) return;
      const attempt = {
        attemptId: globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2),
        source,
        inputFormat: ["pdf", "jpeg", "png"].includes(inputFormat) ? inputFormat : "pdf",
        entry,
        editObserved: false,
        exports: new Set<string>(),
      };
      attempts.set(documentId, attempt);
      emit("pdf_document_added", { attempt_id: attempt.attemptId, source, entry, input_format: attempt.inputFormat });
    },
    edited(documentId: string) {
      const attempt = attempts.get(documentId);
      if (attempt) attempt.editObserved = true;
    },
    exported(documentId: string, format: string, bytes: Uint8Array) {
      const attempt = attempts.get(documentId);
      if (!attempt || bytes.length < 5 ||
          new TextDecoder().decode(bytes.slice(0, 5)) !== "%PDF-") return;
      const key = `${format}:${attempt.editObserved}`;
      if (attempt.exports.has(key)) return;
      attempt.exports.add(key);
      emit("pdf_export_generated", {
        attempt_id: attempt.attemptId,
        source: attempt.source,
        input_format: attempt.inputFormat,
        entry: attempt.entry,
        format,
        committed_edit_observed: attempt.editObserved,
      });
    },
    closed(documentId: string) {
      attempts.delete(documentId);
    },
  };
}

// Correlation is limited to the current page lifetime. No document identifiers,
// file names, field values or bytes are passed to analytics.
export const pdfFirstUse = createPdfFirstUseTracker((event, properties) =>
  posthog.capture(event, properties),
);
